import {calculateErrorMessage} from 'common/helpers'
import {NavigateFunction} from 'react-router'
import store, {persistor} from 'setup/redux/Store'

import {clearMustCreatePassword} from '../selective/mustCreatePasswordHelpers'
import {clearResetSession} from './forgotSetPasswordStorage'
import {goToResetPassword} from './goToResetPassword'
import {PasswordResetCompleteResult, PasswordResetVerifyResult} from './types'

export function getPasswordResetRequestOtpError(
  err: {statusCode?: number; message?: string},
  email?: string
): string {
  if (err?.statusCode === 404) {
    return 'Email not found'
  }

  const message = err?.message ?? ''
  const lower = message.toLowerCase()

  if (
    lower.includes('not found') ||
    lower.includes('no account') ||
    lower.includes('does not exist')
  ) {
    return 'Email not found'
  }

  const emailLooksValid = email ? /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim()) : false
  if (emailLooksValid && lower.includes('validation failed') && lower.includes('email')) {
    return 'Email not found'
  }

  return calculateErrorMessage(err, 'Error sending verification code')
}

export function getPasswordResetVerifyError(
  err: {statusCode?: number; message?: string},
  channel: 'google' | 'otp'
): string {
  // OTP verification: surface whatever the API returned (e.g. "Invalid OTP,
  // please try again"); the hardcoded copy below is Google-specific.
  if (channel === 'otp') {
    return calculateErrorMessage(err, 'Verification failed')
  }

  switch (err?.statusCode) {
    case 400:
      return 'Google account does not match the entered email.'
    case 401:
      return 'Google sign-in failed. Try again or use email OTP.'
    case 404:
      return 'No account found for this Google email.'
    case 422:
      return err.message ?? 'This account is inactive.'
    default:
      return calculateErrorMessage(err, 'Google verification failed')
  }
}

export function handleVerifyPasswordResetSuccess(
  navigate: NavigateFunction,
  result: PasswordResetVerifyResult | undefined
): boolean {
  const {resetSessionToken, emailPurpose, email, username} = result ?? {}
  if (!resetSessionToken || !emailPurpose || !email || !username) {
    return false
  }
  goToResetPassword(navigate, {
    step: 'password_form',
    resetSessionToken,
    emailPurpose,
    email,
    username,
  })
  return true
}

export function handlePasswordResetComplete(res: {result?: PasswordResetCompleteResult}): boolean {
  const redirectUrl = res?.result?.redirectUrl
  if (!redirectUrl) {
    return false
  }
  clearResetSession()
  clearMustCreatePassword(store.dispatch)
  // redirectUrl already carries a fresh, single-use auth_token that Auth.tsx
  // exchanges and applies on load — don't append or duplicate it here, that
  // causes a second concurrent exchange of the same one-time token and races
  // with Auth.tsx's own handoff, tripping the global 401->/logout redirect.
  // Wait for the cleared flag to actually land in persisted storage before the
  // hard navigation unloads this page — otherwise the write can lose the race
  // and the next page load rehydrates with the stale mustCreatePassword=true,
  // bouncing the user back to /create-password in a loop.
  persistor
    .flush()
    .catch(() => {})
    .finally(() => {
      window.location.href = redirectUrl
    })
  return true
}
